import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject, debounceTime } from 'rxjs';
import { MOBILE_BREAKPOINT } from '../constants/app.constants';
import { Contract } from '../models/Contract';
import { ContractParameters } from '../models/ContractParameters';
import { Customer } from '../models/Customer';
import { Dispatch } from '../models/Dispatch';
import { EquipmentListItem } from '../models/EquipmentListItem';
import { DispatchParameters } from '../models/DispatchParameters';
import { FormStatus, LoadBankType, LocationType } from '../models/Enums';
import { DispatchFormState, ErrorState } from '../models/FormState';
import { ListItem } from '../models/ListItem';
import { LoadBankApplyParameters } from '../models/LoadBankApplyParameters';
import { LoadBankItem } from '../models/LoadBankItem';
import { LoadBreakout } from '../models/LoadBreakout';
import { LoadBreakoutParameters } from '../models/LoadBreakoutParameters';
import { LocationListItem } from '../models/LocationListItem';
import { LocationSearchParameters } from '../models/LocationSearchParameters';
import { Schedule } from '../models/Schedule';
import { ContractDataService } from '../services/contract-data.service';
import { HotkeyConfig } from '../models/HotkeyConfig';
import { HOTKEY_SAVE, HOTKEY_SAVE_NEW, HOTKEY_CANCEL, KEY_ENTER } from '../models/HotkeyDefinitions';
import { HotkeyService } from '../services/hotkey.service';
import { ListDataService } from '../services/list-data.service';
import { ScheduleDataService } from '../services/schedule-data.service';

@Component({
    selector: 'dispatch-form',
    templateUrl: 'dispatch-form.component.html',
    styles: [`
    .fs-12 {
        font-size : 12px;
    }  

    `],
    standalone: false
})
export class DispatchFormComponent implements OnInit, OnChanges {
    private readonly EQUIPMENT_WEIGHT_DECIMALS = 3;

    constructor(private _listDataService: ListDataService,
        private _scheduleDataService: ScheduleDataService,
        private _contractDataService: ContractDataService,
        private _hotkeyService: HotkeyService) {

    }

    @ViewChild('closeButton') closeButton: any;
    @ViewChild('locationSearchDropdown') locationSearchDropdownRef!: ElementRef;
    @ViewChild('truckerSearchDropdown') truckerSearchDropdownRef!: ElementRef;
    @ViewChild('contractSearchDropdown') contractSearchDropdownRef!: ElementRef;
    @Input() dispatchParameters: DispatchParameters = new DispatchParameters();
    @Input() dispatchFormId: string = 'dispatchModal';
    @Output() saveComplete: EventEmitter<number> = new EventEmitter<number>();
    @Output() deleteComplete: EventEmitter<number[]> = new EventEmitter<number[]>();

    loadBankApplyFormId: string = this.dispatchFormId + 'LoadBankApply';
    loadBankApplyParameters: LoadBankApplyParameters = new LoadBankApplyParameters();

    errorMessage: string = '';
    scheduleLocationType: string = '';
    dispatchLocationType: string = '';
    loadType: string = '';
    schedule: Schedule = new Schedule();
    dispatch: Dispatch = new Dispatch();
    formState: DispatchFormState = new DispatchFormState();
    locationList: LocationListItem[] = [];
    contractLocations: LocationListItem[] = [];
    filteredLocations: LocationListItem[] = [];
    truckerList: Customer[] = [];
    uomList: ListItem[] = [];
    accountList: ListItem[] = [];
    contractList: Contract[] = [];
    loadNotesError: boolean = false;
    locationNotesError: boolean = false;
    internalNotesError: boolean = false;
    loadNotesMaxLength: number = 200;
    locationNotesMaxLength: number = 1000;
    internalNotesMaxLength: number = 200;
    isNew: boolean = false;
    readonly FormStatus = FormStatus;
    formStatus: FormStatus = FormStatus.Normal;
    showOTC: boolean = false;
    otcNotes: string = '';
    showEquipmentFields: boolean = false;
    isMobile = false
    downArrow = "https://icons8.com/icon/37319/sort-down";
    upArrow = "https://icons8.com/icon/37322/sort-up";
    isShowNotes: boolean = false;
    locationSearchAll: boolean = false;
    locationSearchParms: LocationSearchParameters = new LocationSearchParameters();
    truckerSearchParms: Customer = new Customer();
    showLocationSearch: boolean = false;
    showTruckerSearch: boolean = false;
    showContractSearch: boolean = false;
    breakoutTotal: number = 0;
    favoriteTruckers: Customer[] = [];
    equipmentTypes: EquipmentListItem[] = [];
    private saveClick = new Subject<boolean>();
    private previousLocationForNewDispatch: { location: LocationListItem; customer: Customer; locationNotes: string } | null = null;
    private truckerSearchChange = new Subject<void>();
    locationSearchForm = this.createLocationSearchForm();

    // Dropdown keyboard navigation
    locationHighlightIndex: number = -1;
    truckerHighlightIndex: number = -1;
    contractHighlightIndex: number = -1;

    timeSlots = [
        "", "12:00 AM", "12:30 AM", "01:00 AM", "01:30 AM", "02:00 AM", "02:30 AM",
        "03:00 AM", "03:30 AM", "04:00 AM", "04:30 AM", "05:00 AM", "05:30 AM",
        "06:00 AM", "06:30 AM", "07:00 AM", "07:30 AM", "08:00 AM", "08:30 AM",
        "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
        "12:00 PM", "12:30 PM", "01:00 PM", "01:30 PM", "02:00 PM", "02:30 PM",
        "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM", "05:00 PM", "05:30 PM",
        "06:00 PM", "06:30 PM", "07:00 PM", "07:30 PM", "08:00 PM", "08:30 PM",
        "09:00 PM", "09:30 PM", "10:00 PM", "10:30 PM", "11:00 PM", "11:30 PM"
    ];

    @HostListener('document:click', ['$event'])
    onClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        const isLocationSearchDropdown = target.closest('.location-search-dropdown') !== null;
        const isTruckerSearchDropdown = target.closest('.trucker-search-dropdown') !== null;
        const isContractSearchDropdown = target.closest('.contract-search-dropdown') !== null;
        if (!isLocationSearchDropdown) {
            this.showLocationSearch = false;
            this.clearLocationSearchAll();
            this.clearLocationSearch();
        }
        if (!isTruckerSearchDropdown) {
            this.showTruckerSearch = false;
            this.clearTruckerSearch();
        }
        if (!isContractSearchDropdown) {
            this.showContractSearch = false;
        }
    }

    @HostListener('document:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        // Check if modal is visible
        const modal = document.getElementById(this.dispatchFormId);
        if (!modal || !modal.classList.contains('show')) {
            return;
        }

        // Handle Enter key on checkboxes
        if (event.key === KEY_ENTER && this._hotkeyService.handleCheckboxEnter(event)) {
            return;
        }

        // Handle dropdown navigation
        if (this.showLocationSearch) {
            const handled = this._hotkeyService.handleDropdownKeyboard(event, {
                containerRef: this.locationSearchDropdownRef,
                items: this.locationSearchAll ? this.locationList : this.filteredLocations,
                highlightIndex: this.locationHighlightIndex,
                onHighlightChange: (i) => this.locationHighlightIndex = i,
                onSelect: (item) => this.selectLocation(item),
                onClose: () => { this.showLocationSearch = false; this.locationHighlightIndex = -1; }
            });
            if (handled) return;
        }

        if (this.showTruckerSearch) {
            const handled = this._hotkeyService.handleDropdownKeyboard(event, {
                containerRef: this.truckerSearchDropdownRef,
                items: this.truckerList,
                highlightIndex: this.truckerHighlightIndex,
                onHighlightChange: (i) => this.truckerHighlightIndex = i,
                onSelect: (item) => this.selectTrucker(item),
                onClose: () => { this.showTruckerSearch = false; this.truckerHighlightIndex = -1; }
            });
            if (handled) return;
        }

        if (this.showContractSearch) {
            const handled = this._hotkeyService.handleDropdownKeyboard(event, {
                containerRef: this.contractSearchDropdownRef,
                items: this.contractList,
                highlightIndex: this.contractHighlightIndex,
                onHighlightChange: (i) => this.contractHighlightIndex = i,
                onSelect: (item) => this.selectContract(item),
                onClose: () => { this.showContractSearch = false; this.contractHighlightIndex = -1; }
            });
            if (handled) return;
        }

        // Define hotkeys for this form
        const hotkeys: HotkeyConfig[] = [
            { ...HOTKEY_SAVE_NEW, action: () => this.onSaveAndNew() },
            { ...HOTKEY_SAVE, action: () => this.onSaveAndClose() },
            { ...HOTKEY_CANCEL, action: () => this.onEscapeKey() }
        ];

        this._hotkeyService.handleHotkeys(event, hotkeys);
    }

    private onSaveAndClose(): void {
        if (this.formStatus === FormStatus.Normal) {
            this.onSaveClick(false);
        }
    }

    private onSaveAndNew(): void {
        if (this.formStatus === FormStatus.Normal) {
            this.onSaveClick(true);
        }
    }

    private onEscapeKey(): void {
        if (this.formStatus === FormStatus.Delete) {
            this.cancelDelete();
        } else if (this.formStatus === FormStatus.Normal) {
            this.closeButton.nativeElement.click();
        }
    }

    clearLocationSearch(): void {
        this.locationSearchParms.locationName = "";
        this.locationSearchParms.customerName = "";
        this.locationSearchParms.city = "";
        this.filteredLocations = [...this.contractLocations];
    }

    clearTruckerSearch(): void {
        this.truckerSearchParms.customerName = "";
        this.truckerSearchParms.city = "";
        this.truckerSearchParms.customerCode = "";
        this.truckerList = [...this.favoriteTruckers];
    }

    get hasFavoriteTruckers(): boolean {
        return this.favoriteTruckers.length > 0;
    }

    isFavoriteTrucker(trucker: Customer): boolean {
        return this.favoriteTruckers.some(f => f.customerId === trucker.customerId);
    }

    loadFavoriteTruckers(): void {
        this._listDataService.getFavoriteTruckers().subscribe({
            next: data => {
                this.favoriteTruckers = data;
                const hasSearchCriteria = this.truckerSearchParms.customerName.length > 1 ||
                    this.truckerSearchParms.city.length > 1 ||
                    this.truckerSearchParms.customerCode.length > 1;
                if (this.showTruckerSearch && !hasSearchCriteria) {
                    this.truckerList = [...this.favoriteTruckers];
                }
            },
            error: err => this.errorMessage = err
        });
    }

    toggleLocationSearch() {
        this.showTruckerSearch = false;
        this.showContractSearch = false;
        this.showLocationSearch = !this.showLocationSearch;
        this.locationHighlightIndex = -1;
        this.clearLocationSearchAll();
        this.clearLocationSearch();
    }

    toggleTruckerSearch() {
        this.showLocationSearch = false;
        this.showContractSearch = false;
        this.showTruckerSearch = !this.showTruckerSearch;
        this.truckerHighlightIndex = -1;
        if (this.showTruckerSearch && this.favoriteTruckers.length === 0) {
            this.loadFavoriteTruckers();
        }
        this.clearTruckerSearch();
    }

    toggleContractSearch() {
        this.showLocationSearch = false;
        this.showTruckerSearch = false;
        this.showContractSearch = !this.showContractSearch;
        this.contractHighlightIndex = -1;
    }

    get dispatchBeginDate(): string {
        return this.dispatch.dispatchBeginDate;
    }
    set dispatchBeginDate(value: string) {
        this.dispatch.dispatchBeginDate = value;
        this.validateDispatchDates();
    }
    get dispatchEndDate(): string {
        return this.dispatch.dispatchEndDate;
    }
    set dispatchEndDate(value: string) {
        this.dispatch.dispatchEndDate = value;
        this.validateDispatchDates();
    }
    get dispatchLoads(): number {
        return this.dispatch.loads;
    }
    set dispatchLoads(value: number) {
        this.dispatch.loads = value;
        this.validateLoads();
    }
    get dispatchRate(): string {
        return this.dispatch.rateString;
    }
    set dispatchRate(value: string) {
        this.dispatch.rateString = value.toString();
        const rate = Number(value);
        if (!isNaN(rate)) {
            this.dispatch.rate = rate;
            if (rate > 0 && this.dispatch.rateUnitOfMeasure.length == 0) {
                this.dispatch.rateUnitOfMeasure = this.schedule.commodityUnitOfMeasure;
            }
        }
        this.validateRate();
        this.validateRateUnitOfMeasure();
    }

    get rateUnitOfMeasure(): string {
        return this.dispatch.rateUnitOfMeasure;
    }
    set rateUnitOfMeasure(value: string) {
        this.dispatch.rateUnitOfMeasure = value;
        this.validateRateUnitOfMeasure();
    }
    get loadsBreakout(): boolean {
        return this.dispatch.loadBreakout;
    }
    set loadsBreakout(value: boolean) {
        this.dispatch.loadBreakout = value;
        if (value) {
            this.generateLoadBreakouts();
        }
        else {
            this.clearLoadDays();
        }
    }

    openLoadBank(loadBankType: LoadBankType) {
        let parameters = new LoadBankApplyParameters();
        parameters.parentModal = this.dispatchFormId;
        parameters.locCode = this.dispatchParameters.locCode;
        parameters.loadBankType = loadBankType;
        parameters.commodityCode = this.schedule.commodityCode;

        if (loadBankType == LoadBankType.Load) {
            if (this.schedule.locationType == LocationType.Origin) {
                this.setLoadBankParametersFromSchedule(parameters);
            }
            else {
                this.setLoadBankParametersFromDispatch(parameters);
            }
        }
        else {
            if (this.schedule.locationType == LocationType.Destination) {
                this.setLoadBankParametersFromSchedule(parameters);
            }
            else {
                this.setLoadBankParametersFromDispatch(parameters);
            }
        }
        this.loadBankApplyParameters = parameters;
        return false;
    }

    setLoadBankParametersFromSchedule(parameters: LoadBankApplyParameters) {
        parameters.locationId = this.schedule.location.locationId;
        parameters.locationName = this.schedule.location.locationName;
        parameters.customerId = this.schedule.customer.customerId;
        parameters.customerName = this.schedule.customer.customerName;
        this.schedule.contracts.forEach((contract) => {
            if (!parameters.contractIds.includes(contract.contractId)) {
                parameters.contractIds.push(contract.contractId);
                parameters.contractNumber += contract.contractNumber + ' ';
            }
        });
    }

    setLoadBankParametersFromDispatch(parameters: LoadBankApplyParameters) {
        parameters.locationId = this.dispatch.location.locationId;
        parameters.locationName = this.dispatch.location.locationName;
        parameters.customerId = this.dispatch.customer.customerId;
        parameters.customerName = this.dispatch.customer.customerName;
        if (this.dispatch.contract.contractId > 0) {
            parameters.contractIds.push(this.dispatch.contract.contractId);
            parameters.contractNumber = this.dispatch.contract.contractNumber;
        }
    }

    applyLoadNumber(loadBankItem: LoadBankItem) {
        if (loadBankItem.loadBankType == LoadBankType.Load) {
            this.dispatch.loadNumber = loadBankItem.loadNumber;
            this.dispatch.loadNumberId = loadBankItem.loadBankId;
        }
        else {
            this.dispatch.unloadNumber = loadBankItem.loadNumber;
            this.dispatch.unloadNumberId = loadBankItem.loadBankId;
        }
    }

    createLocationSearchForm() {
        const locationSearchForm = new FormGroup({
            locationName: new FormControl('', { nonNullable: true }),
            city: new FormControl('', { nonNullable: true }),
            customerName: new FormControl('', { nonNullable: true })
        });

        locationSearchForm.valueChanges
            .pipe(debounceTime(1000))
            .subscribe(() => {
                this.searchLocationsAll();
            });
        return locationSearchForm;
    }

    searchLocationsAll() {
        if (this.locationSearchForm.controls.locationName.value.length > 1 ||
            this.locationSearchForm.controls.city.value.length > 1) {
            this._listDataService.searchLocations(this.locationSearchForm.getRawValue()).subscribe({
                next: data => {
                    this.locationList = data;
                },
                error: err => this.errorMessage = err
            });
        }
        else {
            this.locationList = [];
        }
    }

    clearLocationSearchAll() {
        this.locationSearchForm = this.createLocationSearchForm();
        this.locationList = [];
    }

    getContractLocations() {
        if (!this.schedule.locCode || this.schedule.locCode.length == 0) {
            return;
        }

        let parms: ContractParameters = new ContractParameters();
        parms.locCode = this.schedule.locCode;
        parms.commodityCode = this.schedule.commodityCode;
        parms.locationType = this.dispatch.locationType;
        this._contractDataService.getContractLocations(parms).subscribe({
            next: data => {
                this.contractLocations = data;
                this.filteredLocations = data;
            },
            error: err => this.errorMessage = err
        });
    }

    get locationSearchCustomer(): string {
        return this.locationSearchParms.customerName;
    }
    set locationSearchCustomer(value: string) {
        this.locationSearchParms.customerName = value;
        this.filterLocations();
    }
    get locationSearchName(): string {
        return this.locationSearchParms.locationName;
    }
    set locationSearchName(value: string) {
        this.locationSearchParms.locationName = value;
        this.filterLocations();
    }
    get locationSearchCity(): string {
        return this.locationSearchParms.city;
    }
    set locationSearchCity(value: string) {
        this.locationSearchParms.city = value;
        this.filterLocations();
    }

    filterLocations() {
        let filteredList = [...this.contractLocations];

        let customer = this.locationSearchParms.customerName.toLowerCase();
        let location = this.locationSearchParms.locationName.toLowerCase();
        let city = this.locationSearchParms.city.toLowerCase();

        if (customer.length > 0) {
            filteredList = filteredList.filter(item => item.customerName.toLowerCase().includes(customer));
        }
        if (location.length > 0) {
            filteredList = filteredList.filter(item => item.locationName.toLowerCase().includes(location));
        }
        if (city.length > 0) {
            filteredList = filteredList.filter(item => item.city.toLowerCase().includes(city));
        }

        this.filteredLocations = filteredList;
    }

    selectLocation(location: LocationListItem): void {
        this.dispatch.customer.customerId = location.customerId;
        this.dispatch.customer.customerName = location.customerName;
        this.dispatch.location.locationId = location.locationId;
        this.dispatch.location.locationName = location.locationName;
        this.dispatch.location.city = location.city;
        this.showLocationSearch = false;

        //default the location notes
        this._listDataService.getLocation(location.locationId, this.schedule.locCode).subscribe({
            next: data => {
                if (this.dispatch.locationType == LocationType.Origin) {
                    this.dispatch.locationNotes = data.originNotes;
                }
                else {
                    this.dispatch.locationNotes = data.destinationNotes;
                }
            },
            error: err => this.errorMessage = err
        });

        this.validateLocation();
    }

    get truckerSearchName(): string {
        return this.truckerSearchParms.customerName;
    }
    set truckerSearchName(value: string) {
        this.truckerSearchParms.customerName = value;
        this.truckerSearchChange.next();
    }
    get truckerSearchCity(): string {
        return this.truckerSearchParms.city;
    }
    set truckerSearchCity(value: string) {
        this.truckerSearchParms.city = value;
        this.truckerSearchChange.next();
    }
    get truckerSearchCode(): string {
        return this.truckerSearchParms.customerCode;
    }
    set truckerSearchCode(value: string) {
        this.truckerSearchParms.customerCode = value;
        this.truckerSearchChange.next();
    }

    searchTruckers() {
        const hasSearchCriteria = this.truckerSearchParms.customerName.length > 1 ||
            this.truckerSearchParms.city.length > 1 ||
            this.truckerSearchParms.customerCode.length > 1;

        if (!hasSearchCriteria) {
            this.truckerList = [...this.favoriteTruckers];
            return;
        }

        const filteredFavorites = this.getFilteredFavorites();

        this.truckerSearchParms.isTrucker = true;
        this._listDataService.searchCustomers(this.truckerSearchParms).subscribe({
            next: data => {
                const favoriteIds = this.favoriteTruckers.map(f => f.customerId);
                const nonFavorites = data.filter(t => !favoriteIds.includes(t.customerId));

                const sortedFavorites = this.sortByCompliance(filteredFavorites);
                const sortedNonFavorites = this.sortByCompliance(nonFavorites);

                this.truckerList = [...sortedFavorites, ...sortedNonFavorites];
            },
            error: err => this.errorMessage = err
        });
    }

    private sortByCompliance(truckers: Customer[]): Customer[] {
        return truckers.sort((a, b) => {
            // Green (compliant) first
            const aGreen = a.hasCompliance && a.isCompliant ? 1 : 0;
            const bGreen = b.hasCompliance && b.isCompliant ? 1 : 0;
            if (bGreen !== aGreen) return bGreen - aGreen;

            // Yellow (no compliance data) second
            const aYellow = !a.hasCompliance ? 1 : 0;
            const bYellow = !b.hasCompliance ? 1 : 0;
            if (bYellow !== aYellow) return bYellow - aYellow;

            // Then alphabetically
            return a.customerName.localeCompare(b.customerName);
        });
    }

    private getFilteredFavorites(): Customer[] {
        let filteredList = [...this.favoriteTruckers];

        const name = this.truckerSearchParms.customerName.toLowerCase();
        const city = this.truckerSearchParms.city.toLowerCase();
        const code = this.truckerSearchParms.customerCode.toLowerCase();

        if (name.length > 0) {
            filteredList = filteredList.filter(t => t.customerName.toLowerCase().includes(name));
        }
        if (city.length > 0) {
            filteredList = filteredList.filter(t => t.city.toLowerCase().includes(city));
        }
        if (code.length > 0) {
            filteredList = filteredList.filter(t => t.customerCode.toLowerCase().includes(code));
        }

        return filteredList;
    }

    getComplianceClass(trucker: Customer): string {
        if (!trucker.hasCompliance) {
            return 'text-warning';
        }
        if (trucker.isCompliant) {
            return 'text-success';
        }
        else {
            return 'text-danger';
        }
    }

    selectTrucker(trucker: Customer): void {
        this.dispatch.trucker = { ...trucker };
        this.dispatch.currency = this.dispatch.trucker.preferredCurrency;
        this.showTruckerSearch = false;
    }

    getContracts() {
        this.toggleContractSearch();

        if (this.showContractSearch) {
            let contractParms = new ContractParameters();
            contractParms.locCode = this.schedule.locCode;
            contractParms.locationType = this.dispatch.locationType;
            contractParms.commodityCode = this.schedule.commodityCode;
            contractParms.locationId = this.dispatch.location.locationId;
            contractParms.customerId = this.dispatch.customer.customerId;

            this.contractList = [];
            this._contractDataService.getOpenContracts(contractParms).subscribe({
                next: data => {
                    this.contractList = data;
                },
                error: err => this.errorMessage = err
            });
        }
    }

    selectContract(contract: Contract) {
        this.dispatch.contract = { ...contract };
        this.showContractSearch = false;
    }

    removeContract() {
        this.dispatch.contract = new Contract();
        return false;
    }


    generateLoadBreakouts() {
        let parms = new LoadBreakoutParameters();
        parms.dispatchBeginDate = this.dispatch.dispatchBeginDate;
        parms.dispatchEndDate = this.dispatch.dispatchEndDate;
        parms.loads = this.dispatch.loads;
        parms.loadBreakoutList = this.dispatch.loadBreakoutList;

        if (!parms.dispatchBeginDate || parms.dispatchBeginDate.length == 0
            || !parms.dispatchEndDate || parms.dispatchEndDate.length == 0
            || parms.loads <= 0) {
            return;
        }

        this._scheduleDataService.generateLoadBreakouts(parms).subscribe({
            next: data => {
                this.dispatch.loadBreakoutList = data;
                this.calculateBreakoutTotal();
            },
            error: err => this.errorMessage = err
        });
    }

    addLoadDay() {
        let parms = new LoadBreakoutParameters();
        parms.dispatchBeginDate = this.dispatch.dispatchBeginDate;
        parms.dispatchEndDate = this.dispatch.dispatchEndDate;
        parms.loads = this.dispatch.loads;
        parms.loadBreakoutList = this.dispatch.loadBreakoutList;

        this._scheduleDataService.generateLoadBreakout(parms).subscribe({
            next: data => {
                this.dispatch.loadBreakoutList.push(data);
                this.calculateBreakoutTotal();

            },
            error: err => this.errorMessage = err
        });
    }

    clearLoadDays() {
        this.dispatch.loadBreakoutList = [];
        this.calculateBreakoutTotal();
    }

    deleteLoadDay(index: number) {
        if (this.dispatch.loadBreakoutList.length > index) {
            this.dispatch.loadBreakoutList.splice(index, 1);
            this.calculateBreakoutTotal();
        }
        return false;
    }

    setLoadDayOfWeek(loadDay: LoadBreakout) {
        this._listDataService.getWeekDay(loadDay.loadDate).subscribe({
            next: data => {
                loadDay.dayOfWeek = data;
            },
            error: err => this.errorMessage = err
        });
    }

    calculateBreakoutTotal() {
        let totalLoads: number = 0;

        for (let loadDay of this.dispatch.loadBreakoutList) {
            if (loadDay.loads < 0) {
                loadDay.loads = 0;
            }
            totalLoads = totalLoads + loadDay.loads;
        }
        this.breakoutTotal = totalLoads;

        if (this.dispatch.isOTC) {
            this.dispatchLoads = totalLoads > 0 ? totalLoads : 1;
        }

        this.validateLoads();
    }

    validateDispatchDates() {
        if (!this.formState.submitted) {
            return;
        }

        this.formState.beginDate = new ErrorState();
        this.formState.endDate = new ErrorState();
        let startDateValue: string = this.dispatch.dispatchBeginDate;
        let endDateValue: string = this.dispatch.dispatchEndDate;

        if (!startDateValue || startDateValue.length == 0) {
            this.formState.beginDate.invalid = true;
            this.formState.beginDate.errorMessage = 'Dispatch Start Date is required.';
        }
        if (!endDateValue || endDateValue.length == 0) {
            this.formState.endDate.invalid = true;
            this.formState.endDate.errorMessage = 'Dispatch End Date is required.';
        }
        if (this.formState.beginDate.invalid || this.formState.endDate.invalid) {
            return;
        }

        let beginDate: Date = new Date(startDateValue);
        let endDate: Date = new Date(endDateValue);
        if (!isFinite(+beginDate)) {
            this.formState.beginDate.invalid = true;
            this.formState.beginDate.errorMessage = 'Dispatch Start Date must be a valid date.';
        }
        if (!isFinite(+endDate)) {
            this.formState.endDate.invalid = true;
            this.formState.endDate.errorMessage = 'Dispatch End Date must be a valid date.';
        }
        if (this.formState.beginDate.invalid || this.formState.endDate.invalid) {
            return;
        }

        let scheduleBeginDate: Date = new Date(this.schedule.scheduleBeginDate);
        let scheduleEndDate: Date = new Date(this.schedule.scheduleEndDate);
        if (beginDate < scheduleBeginDate) {
            this.formState.beginDate.invalid = true;
            this.formState.beginDate.errorMessage = 'The Dispatch Begin Date must be within the schedule period.';
        }
        if (endDate > scheduleEndDate) {
            this.formState.endDate.invalid = true;
            this.formState.endDate.errorMessage = 'The Dispatch End Date must be within the schedule period.';
        }
        if (this.formState.beginDate.invalid || this.formState.endDate.invalid) {
            return;
        }

        if (endDate < beginDate) {
            this.formState.endDate.invalid = true;
            this.formState.endDate.errorMessage = 'The Dispatch End Date must be after the Dispatch Start Date';
        }
    }

    validateLocation() {
        this.formState.location = new ErrorState();
        if (this.formState.submitted) {
            if (this.dispatch.location.locationId <= 0) {
                this.formState.location.invalid = true;
                this.formState.location.errorMessage = this.dispatchLocationType + ' is required';
            }
        }
    }

    validateLoads() {
        this.formState.loads = new ErrorState();
        if (this.formState.submitted) {
            if (this.dispatch.loads <= 0) {
                this.formState.loads.invalid = true;
                this.formState.loads.errorMessage = 'Number of loads is required';
            }
            else {
                if (this.dispatch.loadBreakout && this.dispatch.loads != this.breakoutTotal) {
                    this.formState.loads.invalid = true;
                    this.formState.loads.errorMessage = 'The breakout by day total loads needs to match the number of loads on the dispatch.';
                }
            }
        }
    }

    validateRate() {
        this.formState.rate = new ErrorState();
        if (this.formState.submitted) {
            const rate: number = Number(this.dispatch.rateString);
            if (isNaN(rate)) {
                this.formState.rate.invalid = true;
                this.formState.rate.errorMessage = "Rate should be a number";
            }
            else if (rate < 0) {
                this.formState.rate.invalid = true;
                this.formState.rate.errorMessage = "Rate cannot be a negative number";
            }
        }
    }

    validateRateUnitOfMeasure() {
        this.formState.rateUnitOfMeasure = new ErrorState();
        if (this.formState.submitted) {
            const rate: number = Number(this.dispatch.rate);
            if (!isNaN(rate) && rate > 0 && (!this.dispatch.rateUnitOfMeasure || this.dispatch.rateUnitOfMeasure.length == 0)) {
                this.formState.rateUnitOfMeasure.invalid = true;
                this.formState.rateUnitOfMeasure.errorMessage = "Rate unit of measure is required";
            }
        }
    }

    validateForm() {
        this.validateDispatchDates();
        this.validateLocation();
        this.validateLoads();
        this.validateRate();
        this.validateRateUnitOfMeasure();
    }

    onSaveClick(newFormSave: boolean = false) {
        this.saveClick.next(newFormSave);
    }

    saveDispatch(newFormSave: boolean): boolean {
        this.formState.submitted = true;
        this.validateForm();

        if (!this.loadNotesError && !this.internalNotesError && !this.locationNotesError && this.formState.isValid()) {
            this.formStatus = FormStatus.Saving;

            this._scheduleDataService.saveDispatch(this.dispatch).subscribe({
                next: data => {
                    this.saveComplete.emit(this.schedule.scheduleId);

                    if (newFormSave) {
                        this.previousLocationForNewDispatch = {
                            location: { ...this.dispatch.location },
                            customer: { ...this.dispatch.customer },
                            locationNotes: this.dispatch.locationNotes
                        };
                        this.setupNewFormState(false);
                        this.dispatchParameters.dispatchId = 0;
                        this.getDispatch();
                    } else {
                        this.closeButton.nativeElement.click();
                    }
                },
                error: err => {
                    this.errorMessage = err;
                    this.formStatus = FormStatus.Normal;
                }
            });
        }
        return false;
    }

    deleteDispatch(): void {
        this.formStatus = FormStatus.Delete;
    }

    cancelDelete(): void {
        this.formStatus = FormStatus.Normal;
    }

    confirmDelete(): void {
        this.formStatus = FormStatus.Deleting;
        this._scheduleDataService.deleteDispatch(this.dispatch.dispatchId).subscribe({
            next: data => {
                this.deleteComplete.emit(data);
                this.closeButton.nativeElement.click();
            },
            error: err => {
                this.errorMessage = err;
                this.formStatus = FormStatus.Normal;
            }
        });
    }

    setLocationTypeName() {
        if (this.schedule.locationType == LocationType.Origin) {
            this.scheduleLocationType = 'Origin';
            this.dispatchLocationType = 'Destination';
            this.loadType = 'Unload';
        }
        else {
            this.scheduleLocationType = 'Destination';
            this.dispatchLocationType = 'Origin';
            this.loadType = 'Load';
        }
    }

    getSchedule(scheduleId: number): void {
        if (scheduleId > 0) {
            this._scheduleDataService.getSchedule(scheduleId).subscribe({
                next: data => {
                    this.schedule = data;
                    this.setLocationTypeName();
                    this.getEquipmentFieldsDefault(this.schedule.locCode);
                    if (this.dispatch.dispatchId == 0) {
                        this.getDispatch();
                    }
                    else {
                        this.getContractLocations();
                    }
                },
                error: err => this.errorMessage = err
            });
        }
    }

    onOTCChange(): void {
        if (this.dispatch.isOTC) {
            this.dispatchLoads = 1
            if (!this.dispatch.loadNotes.includes(this.otcNotes)) {
                this.dispatch.loadNotes = this.dispatch.loadNotes ? `${this.dispatch.loadNotes}\n${this.otcNotes}` : this.otcNotes;
            }
        }
        else {
            const regex = new RegExp(`(\n)?${this.otcNotes}`, 'g');
            this.dispatch.loadNotes = this.dispatch.loadNotes.replace(regex, "");
        }
        if (this.dispatch.loadBreakoutList.length > 0 && this.dispatch.isOTC) {
            this.dispatch.loadBreakoutList = this.dispatch.loadBreakoutList.slice(0, 1);
            this.dispatch.loadBreakoutList[0].loads = 1;
            this.breakoutTotal = 1;
        }
    }

    onEquipmentTypeChange(): void {
        const selected = this.equipmentTypes.find(e => e.equipmentCode === this.dispatch.equipmentType);
        if (selected && selected.defaultWeight !== null) {
            this.dispatch.equipmentWeight = selected.defaultWeight.toFixed(this.EQUIPMENT_WEIGHT_DECIMALS);
            this.dispatch.equipmentWeightUOM = selected.weightUOM ?? '';
        } else {
            this.dispatch.equipmentWeight = '';
            this.dispatch.equipmentWeightUOM = '';
        }
    }

    getDispatch(): void {
        if (this.dispatchParameters.dispatchId > 0) {
            this.isNew = false;
            this._scheduleDataService.getDispatch(this.dispatchParameters.dispatchId).subscribe({
                next: data => {
                    this.dispatch = data;
                    if (this.schedule.scheduleId == 0) {
                        this.getSchedule(this.dispatch.scheduleId);
                    }
                    else {
                        this.getContractLocations();
                    }
                    this.calculateBreakoutTotal();
                },
                error: err => this.errorMessage = err
            });
        }
        else {
            //setup new dispatch
            this.dispatch = new Dispatch();
            if (this.schedule.scheduleId > 0) {
                this.dispatch.scheduleId = this.schedule.scheduleId;
                this.dispatch.dispatchBeginDate = this.schedule.scheduleBeginDate;
                this.dispatch.dispatchEndDate = this.schedule.scheduleEndDate;

                this._scheduleDataService.getDefaultAccountIdForNewDispatch(this.schedule.scheduleId)
                    .subscribe({
                        next: accountId => {
                            this.dispatch.accountId = accountId;
                        },
                        error: err => this.errorMessage = err
                    });

                this.dispatch.locationType = this.schedule.locationType == LocationType.Origin
                    ? LocationType.Destination
                    : LocationType.Origin;

                if (this.schedule.locationType == LocationType.Origin) {
                    this.dispatch.loadNumber = this.schedule.loadNumber;
                }
                else {
                    this.dispatch.unloadNumber = this.schedule.loadNumber;
                }

                if (this.previousLocationForNewDispatch) {
                    this.dispatch.location = { ...this.previousLocationForNewDispatch.location };
                    this.dispatch.customer = { ...this.previousLocationForNewDispatch.customer };
                    this.dispatch.locationNotes = this.previousLocationForNewDispatch.locationNotes;
                    this.previousLocationForNewDispatch = null;
                }

                this.getContractLocations();
            }
            this.breakoutTotal = 0;
        }
    }

    setupNewFormState(resetSchedule: boolean) {
        this.isNew = true;
        this.loadNotesError = false;
        this.internalNotesError = false;
        this.locationNotesError = false;
        this.formStatus = FormStatus.Normal;
        this.formState = new DispatchFormState();
        this.dispatch = new Dispatch();
        this.locationSearchAll = false;
        this.clearLocationSearchAll();
        this.locationSearchParms = new LocationSearchParameters();
        this.contractLocations = [];
        this.filteredLocations = [];
        this.truckerSearchParms = new Customer();
        this.truckerList = [];
        if (resetSchedule) {
            this.favoriteTruckers = [];
        }
        this.isShowNotes = false;
        this.showLocationSearch = false;
        this.showTruckerSearch = false;
        this.showContractSearch = false;
        if (resetSchedule) {
            this.schedule = new Schedule();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['dispatchParameters']) {
            this.setupNewFormState(true);
            if (this.dispatchParameters.scheduleId > 0) {
                this.getSchedule(this.dispatchParameters.scheduleId);
                this.getLocDefaults(this.dispatchParameters.locCode);
                this.loadFavoriteTruckers();
            }
            else {
                this.getDispatch();
            }
        }

        if (changes['dispatchFormId']) {
            this.loadBankApplyFormId = this.dispatchFormId + 'LoadBankApply';
        }
    }

    getLocDefaults(locCode: string): void {
        const keys = ['IsOTCEnabled', 'OTCNote'];

        this._listDataService.getLocDefaults(locCode, keys).subscribe({
            next: data => {
                this.showOTC = data.find(c => c.key == 'IsOTCEnabled')?.value == '1';
                this.otcNotes = data.find(c => c.key == 'OTCNote')?.value ?? '';
            },
            error: err => this.errorMessage = err
        });
    }

    getEquipmentFieldsDefault(locCode: string): void {
        this._listDataService.getLocDefaults(locCode, ['ShowEquipmentFields']).subscribe({
            next: data => {
                this.showEquipmentFields = data.find(c => c.key === 'ShowEquipmentFields')?.value === '1';
            },
            error: err => this.errorMessage = err
        });
    }

    preventNegativeKey(event: KeyboardEvent): void {
        if (event.key === '-' || event.key === 'e') {
            event.preventDefault();
        }
    }

    preventNegative(event: any): void {
        const value = event.target.value;
        event.target.value = value.replace(/[^0-9.]/g, '');
        this.dispatchLoads = event.target.value;
    }

    showHideNotes() {
        this.isShowNotes = !this.isShowNotes;
    }

    ngOnInit(): void {
        if (window.innerWidth <= MOBILE_BREAKPOINT) {
            this.isMobile = true
        }

        this._listDataService.getListItems("account").subscribe({
            next: data => {
                this.accountList = data;
            },
            error: err => this.errorMessage = err
        });

        this._listDataService.getListItems("unitofmeasure").subscribe({
            next: data => {
                this.uomList = data;
            },
            error: err => this.errorMessage = err
        });

        this._listDataService.getEquipmentTypes().subscribe({
            next: data => {
                this.equipmentTypes = data;
            },
            error: err => this.errorMessage = err
        });

        //prevent double click from calling save twice
        this.saveClick.pipe(
            debounceTime(300)
        ).subscribe((newFormSave: boolean) => this.saveDispatch(newFormSave));

        //debounce trucker search to prevent race conditions
        this.truckerSearchChange.pipe(
            debounceTime(300)
        ).subscribe(() => this.searchTruckers());

    }

    private _notesErrorMap: Record<string, 'loadNotesError' | 'locationNotesError' | 'internalNotesError'> = {
        loadNotes: 'loadNotesError',
        locationNotes: 'locationNotesError',
        internalNotes: 'internalNotesError'
    };

    validateLength(field: 'loadNotes' | 'locationNotes' | 'internalNotes', maxLength: number): void {
        if (this.dispatch[field].length > maxLength) {
            this.dispatch[field] = this.dispatch[field].substring(0, maxLength);
            this[this._notesErrorMap[field]] = true;
        } else {
            this[this._notesErrorMap[field]] = false;
        }
    }

} 